import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import {Component} from 'panel';

import {KEY_VALUES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {Tooltip} from '../tooltip';
import template from './index.jade';
import css from './index.styl?inline';

const SIZE = {
  TITLE: `title`,
  XXL: `xxl`,
  XL: `xl`,
  LARGE: `large`,
  MEDIUM: `medium`,
};

export class ClickableInput extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      autocomplete: `string`,
      disabled: `boolean`,
      'error-placement': `string`,
      maxlength: `string`,
      'min-input-width': `string`,
      name: `string`,
      pattern: `string`,
      placeholder: `string`,
      required: `boolean`,
      'select-on-focus': `boolean`,
      size: `string`,
      type: `string`,
    };
  }

  static get SIZES() {
    return Object.values(SIZE);
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        autocomplete: null,
        disabled: false,
        editing: false,
        errorPlacement: `bottom`,
        inputValue: ``,
        maxlength: null,
        minInputWidth: `200px`,
        name: null,
        pattern: null,
        placeholder: ``,
        required: false,
        size: SIZE.XXL,
        type: null,
        validationMessage: null,
        value: ``,
      },
      helpers: {
        getInputAttributes: () => {
          const specifiedAttributes = [
            `autocomplete`,
            `maxlength`,
            `name`,
            `pattern`,
            `placeholder`,
            `required`,
            `type`,
          ].filter((attr) => !isNil(this.state[attr]));
          return pick(this.state, specifiedAttributes);
        },
        handleInput: (ev) => {
          const value = ev.target.value;
          this.update({inputValue: ev.target.value});
          this.dispatchEvent(new CustomEvent(`inputchange`, {detail: {value}}));
          this.checkValidity();
        },
        handleLabelClick: () => this.focusInput(),
        handleInputBlur: () => this.blurInput({requestSave: true}),
        handleKeydown: (ev) => {
          switch (ev.key) {
            case KEY_VALUES.ENTER:
              this.blurInput({requestSave: true});
              break;
            case KEY_VALUES.ESCAPE:
              this.blurInput({requestSave: false});
              break;
          }
        },
        handleInputInvalid: (ev) => {
          ev.preventDefault();
          this.update({validationMessage: ev.target.validationMessage});
        },
        inputInsertHook: (vnode) => {
          this.inputEl = vnode.elm;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback(...arguments);

    // manually delegate focus
    // TODO: use "delegatesFocus" option in Element.attachShadow() in panel
    this.handleHostFocus = () => this.focusInput();
    this.addEventListener(`focus`, this.handleHostFocus);
  }

  disconnectedCallback() {
    this.removeEventListener(`focus`, this.handleHostFocus);
    this.handleHostFocus = null;

    super.disconnectedCallback(...arguments);
  }

  // `select-on-focus` is an attribute but doesn't need to be observed
  // `tabindex` is not a custom attribute but it is supported
  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `autocomplete`:
      case `maxlength`:
      case `pattern`:
      case `placeholder`:
      case `name`:
      case `type`:
        this.update({[attr]: this.attr(attr)});
        break;
      case `disabled`:
        this.blurInput({requestSave: false});
        this.update({disabled: this.attr(attr)});
        break;
      case `error-placement`:
        if (!Tooltip.PLACEMENTS.includes(this.attr(attr))) {
          throw new Error(`error-placement attribute must be one of [${Tooltip.PLACEMENTS.join(`, `)}]`);
        }
        this.update({errorPlacement: this.attr(attr)});
        break;
      case `min-input-width`:
        this.update({minInputWidth: this.attr(attr)});
        break;
      case `required`:
        this.update({required: this.attr(attr)});
        break;
      case `size`:
        if (!ClickableInput.SIZES.includes(this.attr(attr))) {
          throw new Error(`size attribute must be one of [${ClickableInput.SIZES.join(`, `)}]`);
        }
        this.update({size: this.attr(attr)});
        break;
    }
  }

  focusInput() {
    if (!this.state.editing && !this.state.disabled) {
      this.dispatchEvent(new CustomEvent(`inputfocus`));
      this.update({
        editing: true,
        inputValue: this.state.value,
      });
    }

    window.requestAnimationFrame(() => {
      if (this.inputEl !== this.el.activeElement) {
        this.inputEl.focus();
        if (this.attr(`select-on-focus`)) {
          this.inputEl.select();
        }
      }
      this.checkValidity();
    });
  }

  blurInput({requestSave = false}) {
    if (this.state.editing) {
      if (requestSave && this.checkValidity()) {
        this.dispatchEvent(
          new CustomEvent(`save`, {
            detail: {
              value: this.state.inputValue,
            },
          }),
        );
      }
      this.dispatchEvent(new CustomEvent(`inputblur`));
      requestAnimationFrame(() => {
        if (!requestSave) {
          this.update({inputValue: this.value});
        }
      });
    }
    this.update({editing: false});
  }

  checkValidity() {
    const result = this.inputEl.checkValidity();
    if (result && this.state.validationMessage !== null) {
      this.update({validationMessage: null});
    }
    return result;
  }

  get value() {
    return this.state.value;
  }

  set value(newValue) {
    this.update({value: newValue, inputValue: newValue});
  }
}

export const ClickableInputEl = defineAndMakeJsxFactory(`mp-clickable-input`, ClickableInput);
